import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { products } from '../data/products';

const CATEGORIES = [
  { name: 'الإلكترونيات', slug: 'electronics' },
  { name: 'أزياء رجالي', slug: 'mens-fashion' },
  { name: 'أزياء حريمي', slug: 'womens-fashion' },
  { name: 'أزياء الأطفال', slug: 'kids-fashion' },
  { name: 'المنزل والأثاث', slug: 'home-furniture' },
  { name: 'الأجهزة المنزلية', slug: 'home-appliances' },
  { name: 'الجمال والعطور', slug: 'beauty-perfumes' },
  { name: 'العناية بالبشرة', slug: 'skin-care' },
  { name: 'منتجات الأطفال', slug: 'baby-products' },
  { name: 'الألعاب', slug: 'toys' },
  { name: 'مستلزمات رياضية', slug: 'sports' },
];

const Header = () => {
  const { totalItems: cartItems } = useCart();
  const { totalItems: wishlistItems } = useWishlist();

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchBoxRef = useRef(null);
  const navigate = useNavigate();

  // فلترة المنتجات فعلياً كل ما المستخدم يكتب
  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();

    if (query.length === 0) {
      setSuggestions([]);
      return;
    }

    const matches = products
      .filter((product) => product.name.toLowerCase().includes(query))
      .slice(0, 6);

    setSuggestions(matches);
  }, [searchQuery]);

  // إغلاق قائمة الاقتراحات عند الضغط خارجها
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();

    if (searchQuery.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSuggestionClick = (productId) => {
    setShowSuggestions(false);
    setSearchQuery('');
    navigate(`/product/${productId}`);
  };

  return (
    <header className="bg-white shadow-md border-b border-gray-100 sticky top-0 z-50">
      {/* Main Header */}
      <div className="container mx-auto px-3 sm:px-4 py-3 md:py-4">
        <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3 md:gap-4">

          {/* Logo */}
          <div className="order-1 flex-shrink-0 flex items-center gap-2">
            <Link
              to="/"
              className="text-xl sm:text-2xl font-extrabold text-[#1B1B1F]"
              style={{ fontFamily: "'Baloo Bhaijaan 2', sans-serif" }}
            >
              MEME STORE
            </Link>

            <span
              aria-hidden="true"
              className="w-3 h-3 rounded-full bg-[#F2B705]"
            />
          </div>

          {/* Actions (on mobile: same row as logo, on desktop: after search) */}
          <div className="order-2 md:order-3 flex items-center gap-1.5 sm:gap-2">

            {/* Login */}
            <Link
              to="/login"
              className="
                inline-flex items-center gap-2
                h-10 w-10
                md:h-11 md:w-auto md:px-5
                justify-center
                rounded-full
                text-sm font-bold
                bg-[#F2B705]
                text-[#1B1B1F]
                hover:bg-[#1B1B1F]
                hover:text-white
                transition-all
                duration-300
                shadow-sm
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 shrink-0"
              >
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>

              <span className="hidden md:inline">
                تسجيل الدخول
              </span>
            </Link>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              aria-label="المفضلة"
              className="
                relative
                inline-flex
                items-center
                justify-center
                w-10 h-10
                md:w-11 md:h-11
                rounded-full
                text-[#1B1B1F]
                hover:bg-[#F6F5F2]
                transition-all
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              </svg>

              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {wishlistItems}
              </span>
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              aria-label="عربة التسوق"
              className="
                relative
                inline-flex
                items-center
                justify-center
                w-10 h-10
                md:w-11 md:h-11
                rounded-full
                text-[#1B1B1F]
                hover:bg-[#F6F5F2]
                transition-all
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
              >
                <circle cx="8" cy="21" r="1" />
                <circle cx="19" cy="21" r="1" />
                <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
              </svg>

              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#F2B705] text-[#1B1B1F] text-[10px] font-bold rounded-full flex items-center justify-center">
                {cartItems}
              </span>
            </Link>

          </div>

          {/* Search — full width row on mobile, middle on desktop */}
          <div
            ref={searchBoxRef}
            className="order-3 md:order-2 relative w-full md:flex-1 md:max-w-2xl md:mx-4"
          >
            <form onSubmit={handleSearch}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="ما الذي تبحث عنه؟"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  autoComplete="off"
                  className="
                    w-full
                    pl-12
                    pr-5
                    py-2.5
                    md:py-3
                    bg-[#F6F5F2]
                    border
                    border-transparent
                    rounded-full
                    shadow-sm
                    focus:outline-none
                    focus:bg-white
                    focus:border-[#F2B705]
                    focus:ring-2
                    focus:ring-[#F2B705]/30
                    transition-all
                  "
                />

                <button
                  type="submit"
                  aria-label="بحث"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#1B1B1F] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-5 h-5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                </button>
              </div>
            </form>

            {/* قائمة الاقتراحات */}
            {showSuggestions && searchQuery.trim().length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                {suggestions.length > 0 ? (
                  <>
                    <ul>
                      {suggestions.map((product) => (
                        <li key={product.id}>
                          <button
                            type="button"
                            onClick={() => handleSuggestionClick(product.id)}
                            className="w-full flex items-center gap-3 px-4 py-3 text-right hover:bg-[#F6F5F2] transition-colors"
                          >
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-11 h-11 rounded-xl object-cover shrink-0"
                            />
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-bold text-[#1B1B1F] truncate">
                                {product.name}
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                {product.price} ج.م
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={handleSearch}
                      className="w-full text-center text-sm font-bold text-[#1B1B1F] bg-[#F6F5F2] py-3 hover:bg-[#F2B705]/20 transition-colors"
                    >
                      عرض كل نتائج «{searchQuery.trim()}»
                    </button>
                  </>
                ) : (
                  <p className="px-4 py-6 text-center text-sm text-gray-500">
                    لا توجد نتائج مطابقة لـ «{searchQuery.trim()}»
                  </p>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Categories — تظهر كلها بدون تمرير، وتتكسر على أكتر من سطر */}
      <nav className="bg-[#F6F5F2] border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-x-0.5 gap-y-1 py-2">

            <Link
              to="/"
              className="
                shrink-0
                px-2.5
                py-1.5
                rounded-lg
                text-[#1B1B1F]
                text-sm
                font-semibold
                border-b-2
                border-transparent
                hover:border-[#F2B705]
                hover:text-[#F2B705]
                transition-colors
                duration-200
              "
            >
              الرئيسية
            </Link>

            {CATEGORIES.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="
                  shrink-0
                  px-2.5
                  py-1.5
                  rounded-lg
                  text-[#1B1B1F]
                  text-sm
                  font-semibold
                  border-b-2
                  border-transparent
                  hover:border-[#F2B705]
                  hover:text-[#F2B705]
                  transition-colors
                  duration-200
                "
              >
                {cat.name}
              </Link>
            ))}

          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
